import React, { useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import BackLink from '../components/BackLink';
import PageLayout from '../components/ui/PageLayout';
import Panel from '../components/ui/Panel';
import { buttonClass } from '../components/ui/Button';
import { Field, FieldRow, TextArea, labelClass } from '../components/ui/Field';
import Select from '../components/ui/Select';
import Checkbox from '../components/ui/Checkbox';

interface CustomerFormData {
  name: string;
  contact_name: string;
  phone: string;
  contact_email: string;
  shipping_address: string;
  is_lessee: boolean;
  is_renter: boolean;
  is_purchaser: boolean;
  software_terms: string;
  plastics_version: string;
  waiver_type: string;
  notes: string;
}

const CustomerForm: React.FC = () => {
  const history = useHistory();
  const api = useApi();
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<CustomerFormData>({
    name: '',
    contact_name: '',
    phone: '',
    contact_email: '',
    shipping_address: '',
    is_lessee: false,
    is_renter: false,
    is_purchaser: false,
    software_terms: '',
    plastics_version: '',
    waiver_type: '',
    notes: '',
  });

  const set = (key: keyof CustomerFormData, value: any) => setForm(f => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setSaving(true);
    try {
      const created = await api.post('/customers', { body: form });
      toast.success('Customer created');
      history.push(`/customers/${created.id}`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to create customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout kind="focus" className="pt-6">
    <div className="space-y-5">
      <BackLink to="/customers" label="Customers" />

      <h1 className="text-2xl font-bold text-primary">New Customer</h1>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Contact info */}
        <Panel title="Company & Contact" titleStyle="section" accent="primary" lit>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldRow label="Company Name *">
              <Field type="text" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Company name" />
            </FieldRow>
            <FieldRow label="Contact Name">
              <Field type="text" value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="Primary contact" />
            </FieldRow>
            <FieldRow label="Phone">
              <Field type="text" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="Phone number" />
            </FieldRow>
            <FieldRow label="Email">
              <Field type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} placeholder="Email" />
            </FieldRow>
            <FieldRow label="Shipping Address" className="md:col-span-2">
              <TextArea value={form.shipping_address} onChange={e => set('shipping_address', e.target.value)} rows={2} placeholder="Full shipping address" />
            </FieldRow>
          </div>
        </Panel>

        {/* Order configuration */}
        <Panel title="Order Configuration" titleStyle="section" accent="blue" lit>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Status checkboxes */}
            <div className="col-span-2 md:col-span-4">
              <span className={labelClass}>Customer Type</span>
              <div className="mt-1.5 flex flex-wrap gap-4">
                {([
                  { key: 'is_purchaser' as const, label: 'Purchase' },
                  { key: 'is_lessee' as const, label: 'Lease' },
                  { key: 'is_renter' as const, label: 'Rental' },
                ]).map(opt => (
                  <Checkbox
                    key={opt.key}
                    label={opt.label}
                    checked={form[opt.key]}
                    onChange={e => set(opt.key, e.target.checked)}
                  />
                ))}
              </div>
            </div>
            <FieldRow label="SW & Support">
              <Field type="text" value={form.software_terms} onChange={e => set('software_terms', e.target.value)} placeholder="e.g. 3k / mo" />
            </FieldRow>
            <FieldRow label="Plastics">
              <Select
                label="Plastics"
                value={form.plastics_version}
                onChange={key => set('plastics_version', key)}
                options={[{ key: '', label: '--' }, { key: 'Original', label: 'Original' }, { key: 'New', label: 'New' }]}
              />
            </FieldRow>
            <FieldRow label="Waiver">
              <Field type="text" value={form.waiver_type} onChange={e => set('waiver_type', e.target.value)} placeholder="e.g. Standard" />
            </FieldRow>
          </div>
        </Panel>

        {/* Notes */}
        <Panel label="Notes">
          <FieldRow label="Notes">
            <TextArea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} placeholder="Additional notes" />
          </FieldRow>
        </Panel>

        {/* Submit */}
        <div className="flex gap-3">
          <button type="submit" disabled={saving}
            className={buttonClass('filled', 'md')}>
            {saving ? 'Creating...' : 'Create Customer'}
          </button>
          <Link to="/customers"
            className={buttonClass('tonal', 'md')}>
            Cancel
          </Link>
        </div>
      </form>
    </div>
    </PageLayout>
  );
};

export default CustomerForm;
